import type { TAuthStore } from '~/stores/useAuthStore';
import type { UserToken } from '~/types/UserToken';

import Keycloak, { type KeycloakConfig } from 'keycloak-js';
import { jwtDecode } from 'jwt-decode';
import { SESSION_STATUS_ENUM } from '~/enums/Session.enum';

let store: TAuthStore;
let keycloak = new Keycloak();

/**
 * Initializes Keycloak and sets up event listeners.
 * @param kcConfig - Keycloak configuration object.
 * @param onInitCallback - Callback function to be called after initialization.
 * @returns {Promise<void>}
 */
async function init(
  kcConfig: KeycloakConfig,
  onInitCallback: () => void | Promise<void>,
): Promise<void> {
  keycloak = new Keycloak(kcConfig);

  keycloak.onReady = onInitCallback;
  keycloak.onAuthSuccess = updateStore;
  keycloak.onAuthError = (error) => {
    console.error(error);
    store.logout(false, true);
  };
  keycloak.onAuthRefreshSuccess = updateStore;
  keycloak.onAuthRefreshError = () => {
    store.logout(false, true);
  };
  keycloak.onTokenExpired = keycloak.updateToken;
  keycloak.onAuthLogout = () => store.session.interruptSession(SESSION_STATUS_ENUM.LOGGED_OUT);

  await keycloak.init({
    onLoad: 'check-sso',
    pkceMethod: 'S256',
  });
}

/**
 * Initializes store, with keycloak user data.
 * @param storeInstance
 */
function initStore(storeInstance: TAuthStore) {
  store = storeInstance;
}

function updateStore(): void {
  return store.onUpdateOauth(keycloak);
}

/**
 * Login user, then redirect to defined uri.
 */
async function login() {
  try {
    await keycloak.login();
  } catch (error: any) {
    throw new Error(`something wrong logging in`, error);
  }
}

/**
 * Logout user, then redirect to defined uri.
 */
async function logout() {
  await keycloak.logout();
}

// TODO: add disconnect method to be dispatched when couldn't update user token
// TODO: add a not-allowed handler method, to users that have account but not access permission
function isTokenExpired() {
  return keycloak.isTokenExpired();
}

/**
 * Refresh token.
 * @returns { KeycloakInstance } keycloak
 */
async function refreshToken() {
  const refreshed = await keycloak.updateToken();
  if (refreshed) updateStore();
}

/**
 * Defines the tenant based on the provided token.
 * @param token - The token used to decode and determine the tenant.
 * @returns The tenant ID or undefined if the token is invalid.
 */
function defineTenant(token: string): string | undefined {
  const decodedToken: unknown = jwtDecode(token);
  if (!isUserToken(decodedToken)) return undefined;

  const { tenantId } = decodedToken;

  if (tenantId.length > 1) {
    const matriz = tenantId.find((tenantTaxId: string | undefined) =>
      tenantTaxId?.includes('0001/'),
    );

    return matriz ?? tenantId[0];
  }
  return tenantId[0];

  function isUserToken(obj: unknown): obj is UserToken {
    return obj instanceof Object && 'tenantId' in obj && Array.isArray(obj.tenantId);
  }
}

export default {
  CallInit: init,
  CallInitStore: initStore,
  CallUpdateStore: updateStore,
  CallLogin: login,
  CallLogout: logout,
  CallTokenRefresh: keycloak.updateToken,
  CallDefineTenant: defineTenant,
  CallIsTokenExpired: isTokenExpired,
  CallGetToken: () => keycloak.token,
  CallGetRefreshToken: () => keycloak.refreshToken,
  CallIsAuthenticated: () => keycloak.authenticated,
  CallDisconnect: () => store.logout(false, true),
};
